// Texts of the publish area (GIOTHUB-185): the Publish view and the Git
// repository settings. English defines the keys. Menu paths of the Git
// hosts stay in English, as their (mostly English) UIs show them.
import type { Dict } from "./dict";

const en = {
  "pub.status.ready": "frozen, not reviewed",
  "pub.status.reviewed": "reviewed, waiting for your confirmation",
  "pub.status.publishing": "publishing…",
  "pub.status.submitted": "submitted to the store",
  "pub.status.in_review": "in review at Gimasi",
  "pub.status.checks_failed": "the store's checks failed",
  "pub.status.published": "published in the gIoT Store",
  "pub.status.pushed": "pushed to your repository",
  "pub.status.rejected": "rejected by the reviewer",
  "pub.status.failed": "publishing failed",
  "pub.title": "Publish {name}",
  "pub.intro": "A version freezes a draft revision that passes the store's checks. The assistant reviews it, you confirm, then it goes to its destination.",
  "pub.how": "How publishing works",
  "pub.destination": "Destination",
  "pub.git.title": "Your Git repository",
  "pub.git.text": "Private type: your organisation's repository, a tag per version.",
  "pub.store.text": "Public type: reviewed by Gimasi, then available to every tenant.",
  "pub.store.off": "Public publishing is not enabled for your organisation.",
  "pub.saving": "Saving…",
  "pub.saveDestination": "Save destination",
  "pub.versions": "Versions",
  "pub.newVersion": "New version",
  "pub.blockingTitle": "{count} review item(s) block publishing",
  "pub.freezeTitle": "Freeze the current draft as this version",
  "pub.checking": "Checking…",
  "pub.create": "Create {version} from r{rev}",
  "pub.createAny": "Create version from r{rev}",
  "pub.blocking": "{count} review item(s) block publishing: answer the questions and decide the low-confidence keys first.",
  "pub.none": "No version yet.",
  "pub.targetGit": "Git repository",
  "pub.refresh": "Refresh",
  "pub.ai.issues": "AI review: issues found",
  "pub.ai.ready": "AI review: ready",
  "pub.sev.blocker": "blocker",
  "pub.sev.warning": "warning",
  "pub.sev.note": "note",
  "pub.reviewing": "Reviewing…",
  "pub.reviewAgain": "Review again",
  "pub.aiReview": "AI review",
  "pub.confirmGit": "I confirm publishing {version} to our repository",
  "pub.confirmStore": "I confirm publishing {version} to the gIoT Store",
  "pub.despite": "publish despite the issues",
  "pub.publishing": "Publishing…",
  "pub.publish": "Publish",

  "pub.prov.bitbucket": "Bitbucket (access token)",
  "pub.prov.bitbucket_api": "Bitbucket (Atlassian API token)",
  "pub.prov.other": "Other / self-hosted",
  "pub.prov.github.token": "\"Settings > Developer settings > Fine-grained tokens\": this repository, \"Contents: Read and write\" (a classic token needs \"repo\").",
  "pub.prov.github.ssh": "\"Repository > Settings > Deploy keys > Add deploy key\", tick \"Allow write access\".",
  "pub.prov.bitbucket.token": "Repository (or project, workspace) \"Settings > Access tokens\": \"Repositories: Write\".",
  "pub.prov.bitbucket_api.token": "\"Atlassian account > Security > API tokens\" with scopes \"Bitbucket\", \"write:repository\". (App passwords can no longer be created.)",
  "pub.prov.gitlab.token": "\"Project (or group) > Settings > Access tokens\": scope \"write_repository\", role \"Developer\" or higher.",
  "pub.prov.gitlab.ssh": "\"Project > Settings > Repository > Deploy keys\", tick \"Grant write permissions to this key\".",
  "pub.prov.azure.token": "\"User settings > Personal access tokens\": \"Code: Read & write\".",
  "pub.prov.gitea.token": "\"Settings > Applications > Generate token\": \"repository: Read and Write\".",
  "pub.prov.gitea.ssh": "\"Repository > Settings > Deploy keys\", \"Enable write access\".",
  "pub.prov.codecommit.token": "\"IAM > Users > Security credentials > HTTPS Git credentials\": the generated user name and password.",
  "pub.prov.other.token": "A token with write access to the repository; the user name the server expects.",
  "pub.prov.other.ssh": "Add the key to the repository's deploy keys with write access.",
  "pub.git.testedOk": "Saved. The repository answered: the address and the credentials are right (write access shows at the first push).",
  "pub.git.saved": "Saved.",
  "pub.git.hostedOn": "Hosted on",
  "pub.git.auth": "Authentication",
  "pub.git.noSSH": "{provider}: deploy keys cannot push - use a token",
  "pub.git.authToken": "Access token (HTTPS)",
  "pub.git.authSSH": "SSH deploy key",
  "pub.git.repoSSH": "Repository (SSH address)",
  "pub.git.repoHTTPS": "Repository (https)",
  "pub.git.branch": "Branch",
  "pub.git.token": "Access token",
  "pub.git.stored": "· stored",
  "pub.git.tokenPlaceholder": "a token with write access",
  "pub.git.replaceToken": "Replace token",
  "pub.git.user": "User name",
  "pub.git.userPlaceholder": "as the server expects",
  "pub.git.expire": "Fine-grained and API tokens expire: replace the token here when it does.",
  "pub.git.deployKey": "Deploy key",
  "pub.git.keyNew": "· new, not saved yet",
  "pub.git.noKey": "No deploy key yet.",
  "pub.git.thenSave": "Then save: the builder connects once to check it.",
  "pub.git.hostKey": "Server key {key} (pinned: a later change is refused).",
  "pub.git.newKey": "Generate a new key",
  "pub.git.genKey": "Generate a deploy key",
  "pub.git.replacesKey": "a new key replaces the stored one when you save",
  "pub.git.hide": "Hide",
  "pub.git.ca": "Self-hosted server with its own certificate authority?",
  "pub.git.caStoredShort": " (CA stored)",
  "pub.git.caLabel": "CA certificate(s), PEM",
  "pub.git.caStored": "· stored (leave empty to keep)",
  "pub.git.footer": "Credentials are stored encrypted and never shown again. The type goes into the folder {folder} with a tag {tag}; {index} lists the versions. Tenants add the same repository as a type source (read access is enough there).",
  "pub.git.testing": "Testing access…",
  "pub.git.testSave": "Test access and save",
  "pub.git.saveNoTestTitle": "Save without connecting (the first push tests it)",
  "pub.git.saveNoTest": "Save without testing",
} as const;

const de = {
  "pub.status.ready": "eingefroren, nicht geprüft",
  "pub.status.reviewed": "geprüft, wartet auf Ihre Bestätigung",
  "pub.status.publishing": "wird veröffentlicht…",
  "pub.status.submitted": "beim Store eingereicht",
  "pub.status.in_review": "in Prüfung bei Gimasi",
  "pub.status.checks_failed": "die Prüfungen des Stores sind fehlgeschlagen",
  "pub.status.published": "im gIoT Store veröffentlicht",
  "pub.status.pushed": "in Ihr Repository übertragen",
  "pub.status.rejected": "vom Prüfer abgelehnt",
  "pub.status.failed": "Veröffentlichung fehlgeschlagen",
  "pub.title": "{name} veröffentlichen",
  "pub.intro": "Eine Version friert eine Entwurfsrevision ein, die die Prüfungen des Stores besteht. Der Assistent prüft sie, Sie bestätigen, dann geht sie an ihr Ziel.",
  "pub.how": "So funktioniert das Veröffentlichen",
  "pub.destination": "Ziel",
  "pub.git.title": "Ihr Git-Repository",
  "pub.git.text": "Privater Typ: das Repository Ihrer Organisation, ein Tag pro Version.",
  "pub.store.text": "Öffentlicher Typ: von Gimasi geprüft, dann für jeden Mandanten verfügbar.",
  "pub.store.off": "Öffentliches Veröffentlichen ist für Ihre Organisation nicht freigeschaltet.",
  "pub.saving": "Wird gespeichert…",
  "pub.saveDestination": "Ziel speichern",
  "pub.versions": "Versionen",
  "pub.newVersion": "Neue Version",
  "pub.blockingTitle": "{count} Prüfpunkt(e) blockieren das Veröffentlichen",
  "pub.freezeTitle": "Den aktuellen Entwurf als diese Version einfrieren",
  "pub.checking": "Wird geprüft…",
  "pub.create": "{version} aus r{rev} erstellen",
  "pub.createAny": "Version aus r{rev} erstellen",
  "pub.blocking": "{count} Prüfpunkt(e) blockieren das Veröffentlichen: Beantworten Sie zuerst die Fragen und entscheiden Sie über die Schlüssel mit geringer Sicherheit.",
  "pub.none": "Noch keine Version.",
  "pub.targetGit": "Git-Repository",
  "pub.refresh": "Aktualisieren",
  "pub.ai.issues": "KI-Prüfung: Probleme gefunden",
  "pub.ai.ready": "KI-Prüfung: bereit",
  "pub.sev.blocker": "Blocker",
  "pub.sev.warning": "Warnung",
  "pub.sev.note": "Hinweis",
  "pub.reviewing": "Wird geprüft…",
  "pub.reviewAgain": "Erneut prüfen",
  "pub.aiReview": "KI-Prüfung",
  "pub.confirmGit": "Ich bestätige die Veröffentlichung von {version} in unserem Repository",
  "pub.confirmStore": "Ich bestätige die Veröffentlichung von {version} im gIoT Store",
  "pub.despite": "trotz der Probleme veröffentlichen",
  "pub.publishing": "Wird veröffentlicht…",
  "pub.publish": "Veröffentlichen",

  "pub.prov.bitbucket": "Bitbucket (Access Token)",
  "pub.prov.bitbucket_api": "Bitbucket (Atlassian-API-Token)",
  "pub.prov.other": "Andere / selbst gehostet",
  "pub.prov.github.token": "\"Settings > Developer settings > Fine-grained tokens\": dieses Repository, \"Contents: Read and write\" (ein klassisches Token braucht \"repo\").",
  "pub.prov.github.ssh": "\"Repository > Settings > Deploy keys > Add deploy key\", \"Allow write access\" ankreuzen.",
  "pub.prov.bitbucket.token": "Repository- (oder Projekt-, Workspace-) \"Settings > Access tokens\": \"Repositories: Write\".",
  "pub.prov.bitbucket_api.token": "\"Atlassian account > Security > API tokens\" mit den Scopes \"Bitbucket\", \"write:repository\". (App-Passwörter können nicht mehr erstellt werden.)",
  "pub.prov.gitlab.token": "\"Project (oder Group) > Settings > Access tokens\": Scope \"write_repository\", Rolle \"Developer\" oder höher.",
  "pub.prov.gitlab.ssh": "\"Project > Settings > Repository > Deploy keys\", \"Grant write permissions to this key\" ankreuzen.",
  "pub.prov.azure.token": "\"User settings > Personal access tokens\": \"Code: Read & write\".",
  "pub.prov.gitea.token": "\"Settings > Applications > Generate token\": \"repository: Read and Write\".",
  "pub.prov.gitea.ssh": "\"Repository > Settings > Deploy keys\", \"Enable write access\".",
  "pub.prov.codecommit.token": "\"IAM > Users > Security credentials > HTTPS Git credentials\": der erzeugte Benutzername und das Passwort.",
  "pub.prov.other.token": "Ein Token mit Schreibzugriff auf das Repository; der Benutzername, den der Server erwartet.",
  "pub.prov.other.ssh": "Fügen Sie den Schlüssel mit Schreibzugriff zu den Deploy Keys des Repositorys hinzu.",
  "pub.git.testedOk": "Gespeichert. Das Repository hat geantwortet: Adresse und Zugangsdaten stimmen (der Schreibzugriff zeigt sich beim ersten Push).",
  "pub.git.saved": "Gespeichert.",
  "pub.git.hostedOn": "Gehostet bei",
  "pub.git.auth": "Authentifizierung",
  "pub.git.noSSH": "{provider}: Deploy Keys können nicht pushen - verwenden Sie ein Token",
  "pub.git.authToken": "Access Token (HTTPS)",
  "pub.git.authSSH": "SSH-Deploy-Key",
  "pub.git.repoSSH": "Repository (SSH-Adresse)",
  "pub.git.repoHTTPS": "Repository (https)",
  "pub.git.branch": "Branch",
  "pub.git.token": "Access Token",
  "pub.git.stored": "· gespeichert",
  "pub.git.tokenPlaceholder": "ein Token mit Schreibzugriff",
  "pub.git.replaceToken": "Token ersetzen",
  "pub.git.user": "Benutzername",
  "pub.git.userPlaceholder": "wie vom Server erwartet",
  "pub.git.expire": "Fine-grained- und API-Tokens laufen ab: Ersetzen Sie das Token dann hier.",
  "pub.git.deployKey": "Deploy Key",
  "pub.git.keyNew": "· neu, noch nicht gespeichert",
  "pub.git.noKey": "Noch kein Deploy Key.",
  "pub.git.thenSave": "Dann speichern: Der Builder verbindet sich einmal, um ihn zu prüfen.",
  "pub.git.hostKey": "Serverschlüssel {key} (fixiert: eine spätere Änderung wird abgelehnt).",
  "pub.git.newKey": "Neuen Schlüssel erzeugen",
  "pub.git.genKey": "Deploy Key erzeugen",
  "pub.git.replacesKey": "ein neuer Schlüssel ersetzt beim Speichern den gespeicherten",
  "pub.git.hide": "Ausblenden",
  "pub.git.ca": "Selbst gehosteter Server mit eigener Zertifizierungsstelle?",
  "pub.git.caStoredShort": " (CA gespeichert)",
  "pub.git.caLabel": "CA-Zertifikat(e), PEM",
  "pub.git.caStored": "· gespeichert (leer lassen, um es zu behalten)",
  "pub.git.footer": "Zugangsdaten werden verschlüsselt gespeichert und nie wieder angezeigt. Der Typ kommt in den Ordner {folder} mit einem Tag {tag}; {index} listet die Versionen. Mandanten fügen dasselbe Repository als Typquelle hinzu (dort genügt Lesezugriff).",
  "pub.git.testing": "Zugriff wird getestet…",
  "pub.git.testSave": "Zugriff testen und speichern",
  "pub.git.saveNoTestTitle": "Ohne Verbindung speichern (der erste Push testet es)",
  "pub.git.saveNoTest": "Ohne Test speichern",
} satisfies Dict<typeof en>;

const it = {
  "pub.status.ready": "congelata, non revisionata",
  "pub.status.reviewed": "revisionata, in attesa della sua conferma",
  "pub.status.publishing": "pubblicazione in corso…",
  "pub.status.submitted": "inviata allo store",
  "pub.status.in_review": "in revisione presso Gimasi",
  "pub.status.checks_failed": "i controlli dello store non sono stati superati",
  "pub.status.published": "pubblicata nel gIoT Store",
  "pub.status.pushed": "inviata al suo repository",
  "pub.status.rejected": "rifiutata dal revisore",
  "pub.status.failed": "pubblicazione non riuscita",
  "pub.title": "Pubblica {name}",
  "pub.intro": "Una versione congela una revisione della bozza che supera i controlli dello store. L'assistente la esamina, lei conferma, poi va alla sua destinazione.",
  "pub.how": "Come funziona la pubblicazione",
  "pub.destination": "Destinazione",
  "pub.git.title": "Il suo repository Git",
  "pub.git.text": "Tipo privato: il repository della sua organizzazione, un tag per versione.",
  "pub.store.text": "Tipo pubblico: revisionato da Gimasi, poi disponibile per ogni tenant.",
  "pub.store.off": "La pubblicazione pubblica non è abilitata per la sua organizzazione.",
  "pub.saving": "Salvataggio…",
  "pub.saveDestination": "Salva destinazione",
  "pub.versions": "Versioni",
  "pub.newVersion": "Nuova versione",
  "pub.blockingTitle": "{count} elemento/i di revisione bloccano la pubblicazione",
  "pub.freezeTitle": "Congela la bozza attuale come questa versione",
  "pub.checking": "Verifica…",
  "pub.create": "Crea {version} da r{rev}",
  "pub.createAny": "Crea versione da r{rev}",
  "pub.blocking": "{count} elemento/i di revisione bloccano la pubblicazione: risponda prima alle domande e decida sulle chiavi a bassa confidenza.",
  "pub.none": "Ancora nessuna versione.",
  "pub.targetGit": "Repository Git",
  "pub.refresh": "Aggiorna",
  "pub.ai.issues": "Revisione IA: problemi trovati",
  "pub.ai.ready": "Revisione IA: pronta",
  "pub.sev.blocker": "bloccante",
  "pub.sev.warning": "avviso",
  "pub.sev.note": "nota",
  "pub.reviewing": "Revisione in corso…",
  "pub.reviewAgain": "Rivedi di nuovo",
  "pub.aiReview": "Revisione IA",
  "pub.confirmGit": "Confermo la pubblicazione di {version} nel nostro repository",
  "pub.confirmStore": "Confermo la pubblicazione di {version} nel gIoT Store",
  "pub.despite": "pubblica nonostante i problemi",
  "pub.publishing": "Pubblicazione…",
  "pub.publish": "Pubblica",

  "pub.prov.bitbucket": "Bitbucket (access token)",
  "pub.prov.bitbucket_api": "Bitbucket (token API Atlassian)",
  "pub.prov.other": "Altro / self-hosted",
  "pub.prov.github.token": "\"Settings > Developer settings > Fine-grained tokens\": questo repository, \"Contents: Read and write\" (un token classico richiede \"repo\").",
  "pub.prov.github.ssh": "\"Repository > Settings > Deploy keys > Add deploy key\", selezioni \"Allow write access\".",
  "pub.prov.bitbucket.token": "\"Settings > Access tokens\" del repository (o progetto, workspace): \"Repositories: Write\".",
  "pub.prov.bitbucket_api.token": "\"Atlassian account > Security > API tokens\" con gli scope \"Bitbucket\", \"write:repository\". (Le app password non si possono più creare.)",
  "pub.prov.gitlab.token": "\"Project (o group) > Settings > Access tokens\": scope \"write_repository\", ruolo \"Developer\" o superiore.",
  "pub.prov.gitlab.ssh": "\"Project > Settings > Repository > Deploy keys\", selezioni \"Grant write permissions to this key\".",
  "pub.prov.azure.token": "\"User settings > Personal access tokens\": \"Code: Read & write\".",
  "pub.prov.gitea.token": "\"Settings > Applications > Generate token\": \"repository: Read and Write\".",
  "pub.prov.gitea.ssh": "\"Repository > Settings > Deploy keys\", \"Enable write access\".",
  "pub.prov.codecommit.token": "\"IAM > Users > Security credentials > HTTPS Git credentials\": il nome utente e la password generati.",
  "pub.prov.other.token": "Un token con accesso in scrittura al repository; il nome utente che il server si aspetta.",
  "pub.prov.other.ssh": "Aggiunga la chiave alle deploy key del repository con accesso in scrittura.",
  "pub.git.testedOk": "Salvato. Il repository ha risposto: indirizzo e credenziali sono corretti (l'accesso in scrittura si vede al primo push).",
  "pub.git.saved": "Salvato.",
  "pub.git.hostedOn": "Ospitato su",
  "pub.git.auth": "Autenticazione",
  "pub.git.noSSH": "{provider}: le deploy key non possono fare push - usi un token",
  "pub.git.authToken": "Access token (HTTPS)",
  "pub.git.authSSH": "Deploy key SSH",
  "pub.git.repoSSH": "Repository (indirizzo SSH)",
  "pub.git.repoHTTPS": "Repository (https)",
  "pub.git.branch": "Branch",
  "pub.git.token": "Access token",
  "pub.git.stored": "· salvato",
  "pub.git.tokenPlaceholder": "un token con accesso in scrittura",
  "pub.git.replaceToken": "Sostituisci token",
  "pub.git.user": "Nome utente",
  "pub.git.userPlaceholder": "come richiesto dal server",
  "pub.git.expire": "I token fine-grained e API scadono: in tal caso sostituisca qui il token.",
  "pub.git.deployKey": "Deploy key",
  "pub.git.keyNew": "· nuova, non ancora salvata",
  "pub.git.noKey": "Ancora nessuna deploy key.",
  "pub.git.thenSave": "Poi salvi: il builder si connette una volta per verificarla.",
  "pub.git.hostKey": "Chiave del server {key} (fissata: una modifica successiva viene rifiutata).",
  "pub.git.newKey": "Genera una nuova chiave",
  "pub.git.genKey": "Genera una deploy key",
  "pub.git.replacesKey": "una nuova chiave sostituisce quella salvata al salvataggio",
  "pub.git.hide": "Nascondi",
  "pub.git.ca": "Server self-hosted con una propria autorità di certificazione?",
  "pub.git.caStoredShort": " (CA salvata)",
  "pub.git.caLabel": "Certificato/i CA, PEM",
  "pub.git.caStored": "· salvato (lasci vuoto per mantenerlo)",
  "pub.git.footer": "Le credenziali sono salvate cifrate e non vengono più mostrate. Il tipo va nella cartella {folder} con un tag {tag}; {index} elenca le versioni. I tenant aggiungono lo stesso repository come sorgente di tipi (lì basta l'accesso in lettura).",
  "pub.git.testing": "Verifica dell'accesso…",
  "pub.git.testSave": "Verifica accesso e salva",
  "pub.git.saveNoTestTitle": "Salva senza connettersi (il primo push lo verifica)",
  "pub.git.saveNoTest": "Salva senza verifica",
} satisfies Dict<typeof en>;

const fr = {
  "pub.status.ready": "figée, non revue",
  "pub.status.reviewed": "revue, en attente de votre confirmation",
  "pub.status.publishing": "publication…",
  "pub.status.submitted": "soumise au store",
  "pub.status.in_review": "en revue chez Gimasi",
  "pub.status.checks_failed": "les contrôles du store ont échoué",
  "pub.status.published": "publiée dans le gIoT Store",
  "pub.status.pushed": "poussée dans votre dépôt",
  "pub.status.rejected": "refusée par le relecteur",
  "pub.status.failed": "échec de la publication",
  "pub.title": "Publier {name}",
  "pub.intro": "Une version fige une révision du brouillon qui passe les contrôles du store. L'assistant la revoit, vous confirmez, puis elle part vers sa destination.",
  "pub.how": "Comment fonctionne la publication",
  "pub.destination": "Destination",
  "pub.git.title": "Votre dépôt Git",
  "pub.git.text": "Type privé : le dépôt de votre organisation, un tag par version.",
  "pub.store.text": "Type public : revu par Gimasi, puis disponible pour chaque tenant.",
  "pub.store.off": "La publication publique n'est pas activée pour votre organisation.",
  "pub.saving": "Enregistrement…",
  "pub.saveDestination": "Enregistrer la destination",
  "pub.versions": "Versions",
  "pub.newVersion": "Nouvelle version",
  "pub.blockingTitle": "{count} élément(s) de revue bloquent la publication",
  "pub.freezeTitle": "Figer le brouillon actuel comme cette version",
  "pub.checking": "Vérification…",
  "pub.create": "Créer {version} depuis r{rev}",
  "pub.createAny": "Créer une version depuis r{rev}",
  "pub.blocking": "{count} élément(s) de revue bloquent la publication : répondez d'abord aux questions et tranchez les clés à faible confiance.",
  "pub.none": "Aucune version pour l'instant.",
  "pub.targetGit": "Dépôt Git",
  "pub.refresh": "Actualiser",
  "pub.ai.issues": "Revue IA : problèmes trouvés",
  "pub.ai.ready": "Revue IA : prête",
  "pub.sev.blocker": "bloquant",
  "pub.sev.warning": "avertissement",
  "pub.sev.note": "remarque",
  "pub.reviewing": "Revue en cours…",
  "pub.reviewAgain": "Revoir à nouveau",
  "pub.aiReview": "Revue IA",
  "pub.confirmGit": "Je confirme la publication de {version} dans notre dépôt",
  "pub.confirmStore": "Je confirme la publication de {version} dans le gIoT Store",
  "pub.despite": "publier malgré les problèmes",
  "pub.publishing": "Publication…",
  "pub.publish": "Publier",

  "pub.prov.bitbucket": "Bitbucket (access token)",
  "pub.prov.bitbucket_api": "Bitbucket (jeton API Atlassian)",
  "pub.prov.other": "Autre / auto-hébergé",
  "pub.prov.github.token": "« Settings > Developer settings > Fine-grained tokens » : ce dépôt, « Contents: Read and write » (un jeton classique demande « repo »).",
  "pub.prov.github.ssh": "« Repository > Settings > Deploy keys > Add deploy key », cochez « Allow write access ».",
  "pub.prov.bitbucket.token": "« Settings > Access tokens » du dépôt (ou du projet, du workspace) : « Repositories: Write ».",
  "pub.prov.bitbucket_api.token": "« Atlassian account > Security > API tokens » avec les scopes « Bitbucket », « write:repository ». (Les mots de passe d'application ne peuvent plus être créés.)",
  "pub.prov.gitlab.token": "« Project (ou group) > Settings > Access tokens » : scope « write_repository », rôle « Developer » ou supérieur.",
  "pub.prov.gitlab.ssh": "« Project > Settings > Repository > Deploy keys », cochez « Grant write permissions to this key ».",
  "pub.prov.azure.token": "« User settings > Personal access tokens » : « Code: Read & write ».",
  "pub.prov.gitea.token": "« Settings > Applications > Generate token » : « repository: Read and Write ».",
  "pub.prov.gitea.ssh": "« Repository > Settings > Deploy keys », « Enable write access ».",
  "pub.prov.codecommit.token": "« IAM > Users > Security credentials > HTTPS Git credentials » : le nom d'utilisateur et le mot de passe générés.",
  "pub.prov.other.token": "Un jeton avec accès en écriture au dépôt ; le nom d'utilisateur attendu par le serveur.",
  "pub.prov.other.ssh": "Ajoutez la clé aux deploy keys du dépôt avec accès en écriture.",
  "pub.git.testedOk": "Enregistré. Le dépôt a répondu : l'adresse et les identifiants sont corrects (l'accès en écriture se vérifie au premier push).",
  "pub.git.saved": "Enregistré.",
  "pub.git.hostedOn": "Hébergé sur",
  "pub.git.auth": "Authentification",
  "pub.git.noSSH": "{provider} : les deploy keys ne peuvent pas pousser - utilisez un jeton",
  "pub.git.authToken": "Jeton d'accès (HTTPS)",
  "pub.git.authSSH": "Deploy key SSH",
  "pub.git.repoSSH": "Dépôt (adresse SSH)",
  "pub.git.repoHTTPS": "Dépôt (https)",
  "pub.git.branch": "Branche",
  "pub.git.token": "Jeton d'accès",
  "pub.git.stored": "· enregistré",
  "pub.git.tokenPlaceholder": "un jeton avec accès en écriture",
  "pub.git.replaceToken": "Remplacer le jeton",
  "pub.git.user": "Nom d'utilisateur",
  "pub.git.userPlaceholder": "comme l'attend le serveur",
  "pub.git.expire": "Les jetons fine-grained et API expirent : remplacez alors le jeton ici.",
  "pub.git.deployKey": "Deploy key",
  "pub.git.keyNew": "· nouvelle, pas encore enregistrée",
  "pub.git.noKey": "Pas encore de deploy key.",
  "pub.git.thenSave": "Puis enregistrez : le builder se connecte une fois pour la vérifier.",
  "pub.git.hostKey": "Clé du serveur {key} (épinglée : un changement ultérieur est refusé).",
  "pub.git.newKey": "Générer une nouvelle clé",
  "pub.git.genKey": "Générer une deploy key",
  "pub.git.replacesKey": "une nouvelle clé remplace celle enregistrée lors de l'enregistrement",
  "pub.git.hide": "Masquer",
  "pub.git.ca": "Serveur auto-hébergé avec sa propre autorité de certification ?",
  "pub.git.caStoredShort": " (CA enregistrée)",
  "pub.git.caLabel": "Certificat(s) CA, PEM",
  "pub.git.caStored": "· enregistré (laisser vide pour le conserver)",
  "pub.git.footer": "Les identifiants sont enregistrés chiffrés et ne sont plus jamais affichés. Le type va dans le dossier {folder} avec un tag {tag} ; {index} liste les versions. Les tenants ajoutent le même dépôt comme source de types (l'accès en lecture y suffit).",
  "pub.git.testing": "Test de l'accès…",
  "pub.git.testSave": "Tester l'accès et enregistrer",
  "pub.git.saveNoTestTitle": "Enregistrer sans se connecter (le premier push le teste)",
  "pub.git.saveNoTest": "Enregistrer sans tester",
} satisfies Dict<typeof en>;

export default { en, de, it, fr };
